import React, {
  useState,
  useEffect,
  useRef,
} from 'react'

import {
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom'

import useTitle from '../../hooks/useTitle'

// 17 Jul 2026
// Trinity logo
import EtEtTrinityIcon6SVGComp from '@/assets/img/icons/svgComp/EtEtTrinityIcon6SVGComp'


// const serverUrl = process.env.SERVER_URL || 'http://localhost:4500'
// const serverUrl = 'https://etwebapp1-be.onrender.com'
const serverUrl = process.env.REACT_APP_SERVER_URL


const successHeader = 'Congratulations!'

const successMsg =
  'You have successfully reset the password and are ready to sign in.'


// 0a - Password Rules - 17 Jul 2026
const getPasswordRules = (password) => ({
  minLength: password.length >= 8,
  uppercase: /[A-Z]/.test(password),
  number: /\d/.test(password),
  specialCharacter:
    /[^A-Za-z0-9]/.test(password),
})


// 0b - Password Strength - 17 Jul 2026
const getPasswordStrength = (
  password,
  passwordRules
) => {
  if (!password) {
    return {
      label: '',
      level: 0,
    }
  }

  const requiredRulesPassed =
    Object.values(passwordRules).filter(Boolean).length

  const allRequiredRulesPassed =
    Object.values(passwordRules).every(Boolean)

  // Weak until all mandatory password rules are satisfied.
  if (!allRequiredRulesPassed) {
    return {
      label: 'Weak',
      level: Math.max(
        1,
        Math.min(requiredRulesPassed, 2)
      ),
    }
  }

  // A password becomes strong when it satisfies all
  // mandatory rules and adds length and character variety.
  const hasLowercase = /[a-z]/.test(password)
  const hasExtendedLength = password.length >= 12

  if (hasLowercase && hasExtendedLength) {
    return {
      label: 'Strong',
      level: 3,
    }
  }

  return {
    label: 'Good',
    level: 2,
  }
}


// 0c - Password Field Component - 17 Jul 2026
const PasswordField = ({
  id,
  label,
  value,
  onChange,
  visible,
  onToggle,
  autoComplete,
  describedBy,
  isDark,
}) => {
  const inputClass = isDark
    ? 'border-gray-700 text-white placeholder:text-gray-600 hover:border-gray-600 focus:border-orange-500'
    : 'border-gray-300 text-gray-900 placeholder:text-gray-400 hover:border-gray-400 focus:border-orange-500'

  const visibilityButtonClass = isDark
    ? 'text-gray-500 hover:bg-white/[0.06] hover:text-gray-200'
    : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'

  return (
    <div className="group relative">
      <LockIcon isDark={isDark} />

      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={label}
        aria-label={label}
        aria-describedby={describedBy}
        autoComplete={autoComplete}
        required
        className={`w-full border-0 border-b bg-transparent py-3 pl-9 pr-12 text-sm outline-none transition focus:ring-0 ${inputClass}`}
      />

      <button
        type="button"
        onClick={onToggle}
        aria-label={
          visible
            ? `Hide ${label.toLowerCase()}`
            : `Show ${label.toLowerCase()}`
        }
        className={`absolute right-0 top-1/2 -translate-y-1/2 rounded-full p-2 transition focus:outline-none ${visibilityButtonClass}`}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  )
}


// 0d - Password Rule Item - 17 Jul 2026
const PasswordRuleItem = ({
  met,
  children,
  isDark,
}) => (
  <li
    className={`flex items-center gap-2 text-[11px] leading-5 transition-colors duration-300 ${
      met
        ? isDark
          ? 'text-emerald-400'
          : 'text-emerald-700'
        : isDark
          ? 'text-gray-500'
          : 'text-gray-500'
    }`}
  >
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
        met
          ? isDark
            ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-400'
            : 'border-emerald-500/40 bg-emerald-50 text-emerald-700'
          : isDark
            ? 'border-gray-700 text-gray-600'
            : 'border-gray-300 text-gray-400'
      }`}
      aria-hidden="true"
    >
      {met ? (
        <CheckIcon />
      ) : (
        <span className="h-1 w-1 rounded-full bg-current" />
      )}
    </span>

    <span>{children}</span>
  </li>
)


// 0e - Password Icon - 17 Jul 2026
const LockIcon = ({ isDark }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    className={`absolute left-0 top-1/2 h-5 w-5 -translate-y-1/2 transition group-focus-within:text-orange-500 ${
      isDark
        ? 'text-gray-600'
        : 'text-gray-400'
    }`}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M7.5 10.5V8.25a4.5 4.5 0 019 0v2.25M6.75 10.5h10.5A1.75 1.75 0 0119 12.25v7A1.75 1.75 0 0117.25 21H6.75A1.75 1.75 0 015 19.25v-7a1.75 1.75 0 011.75-1.75Z"
    />
  </svg>
)


// 0f - Show Password Icon - 17 Jul 2026
const EyeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 12s3.5-6 9.75-6 9.75 6 9.75 6-3.5 6-9.75 6-9.75-6-9.75-6Z"
    />

    <circle
      cx="12"
      cy="12"
      r="2.75"
    />
  </svg>
)


// 0g - Hide Password Icon - 17 Jul 2026
const EyeOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m3 3 18 18M10.6 10.7A2.8 2.8 0 0012 15a2.8 2.8 0 002-4.8M9.9 5.3A10.7 10.7 0 0112 5c6.25 0 9.75 7 9.75 7a17 17 0 01-2.4 3.25M6.4 6.4C3.7 8.3 2.25 12 2.25 12s3.5 7 9.75 7a10.6 10.6 0 004.15-.85"
    />
  </svg>
)


// 0h - Check Icon - 17 Jul 2026
const CheckIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-2.5 w-2.5"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m3.5 8 3 3 6-6"
    />
  </svg>
)


// 0i - Button Arrow Icon - 17 Jul 2026
const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 12h14m-5-5 5 5-5 5"
    />
  </svg>
)


// 0j - Success Icon - 17 Jul 2026
const SuccessIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-7 w-7"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m5.5 12.5 4 4 9-9"
    />
  </svg>
)


// 0k - Light Theme Icon - 17 Jul 2026
const SunIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-4 w-4"
    aria-hidden="true"
  >
    <circle
      cx="12"
      cy="12"
      r="3.5"
    />

    <path
      strokeLinecap="round"
      d="M12 2.5v2M12 19.5v2M4.5 12h-2M21.5 12h-2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M18.7 5.3l-1.4 1.4M6.7 17.3l-1.4 1.4"
    />
  </svg>
)


// 0l - Dark Theme Icon - 17 Jul 2026
const MoonIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-4 w-4"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20.2 15.2A8.2 8.2 0 018.8 3.8 8.7 8.7 0 1020.2 15.2Z"
    />
  </svg>
)


const ResetPassword = () => {
  // Author - Atul Pandey
  // Updated - 17 Jul 2026 - refined visual appeal
  // Updated - 17 Jul 2026 - added light and dark theme toggle
  // Updated - 17 Jul 2026 - expanded password input width
  // Updated - 17 Jul 2026 - simplified to a centered reset password dialog
  // Updated - 17 Jul 2026 - aligned visual design with Login and Forgot Password pages
  // Updated - 17 Jul 2026 - replaced success modal with fixed high z-index confirmation overlay
  // Updated - 17 Jul 2026 - added live password rules and strength validation

  // 0 - Use Title
  useTitle('Reset Password')

  // 1a - Navigation and Location
  const navigate = useNavigate()
  const location = useLocation()

  // 1b - Modal Reference - 17 Jul 2026
  const confirmButtonRef = useRef(null)

  // 1c - Password Reset State
  const [resetToken, setResetToken] =
    useState('')

  const [newPassword, setNewPassword] =
    useState('')

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState('')

  const [
    resetPasswordSuccess,
    setResetPasswordSuccess,
  ] = useState(false)

  const [
    resetPasswordMessage,
    setResetPasswordMessage,
  ] = useState('')

  // 1d - Request State - 17 Jul 2026
  const [isSubmitting, setIsSubmitting] =
    useState(false)

  // 1e - Password Visibility State - 17 Jul 2026
  const [
    showNewPassword,
    setShowNewPassword,
  ] = useState(false)

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false)

  // 1f - Theme State - 17 Jul 2026
  // Light theme remains the default each time the page opens.
  const [isDark, setIsDark] = useState(false)

  // 2a - Password Validation - 17 Jul 2026
  const passwordRules =
    getPasswordRules(newPassword)

  const isPasswordValid =
    Object.values(passwordRules).every(Boolean)

  const passwordsMatch =
    confirmPassword.length > 0 &&
    newPassword === confirmPassword

  const passwordStrength =
    getPasswordStrength(
      newPassword,
      passwordRules
    )

  const canSubmit =
    Boolean(resetToken) &&
    isPasswordValid &&
    passwordsMatch &&
    !isSubmitting

  // 2b - Theme Based Class Variables - 17 Jul 2026
  const pageClass = isDark
    ? 'bg-[#0d0d0e] text-white'
    : 'bg-gradient-to-b from-white via-gray-50 to-orange-50/60 text-gray-900'

  const mainTextClass = isDark
    ? 'text-white'
    : 'text-gray-900'

  const mutedTextClass = isDark
    ? 'text-gray-500'
    : 'text-gray-500'

  const secondaryTextClass = isDark
    ? 'text-gray-600'
    : 'text-gray-400'

  const dialogClass = isDark
    ? 'border-white/[0.08] bg-[#141415]/90 shadow-black/40'
    : 'border-gray-200/80 bg-white/90 shadow-gray-300/50'

  const passwordInputMessageClass = isDark
    ? 'bg-red-500/10 text-red-300'
    : 'bg-red-50 text-red-700'

  const dividerClass = isDark
    ? 'border-gray-800'
    : 'border-gray-200'

  const themeToggleContainerClass = isDark
    ? 'border-white/10 bg-white/[0.05]'
    : 'border-gray-200 bg-white/75'

  const confirmationDialogClass = isDark
    ? 'border-white/[0.1] bg-[#171718] shadow-black/70'
    : 'border-gray-200 bg-white shadow-gray-900/25'

  const guidanceContainerClass = isDark
    ? 'border-white/[0.07] bg-white/[0.025]'
    : 'border-gray-200 bg-gray-50/70'

  // 2c - Handle Modal Confirmation
  const handleConfirm = () => {
    navigate('/login', {
      replace: true,
    })
  }

  // 2d - Handle Theme Toggle - 17 Jul 2026
  const handleThemeToggle = () =>
    setIsDark((prev) => !prev)

  // 3a - Extract Reset Token from URL
  useEffect(() => {
    const searchParams =
      new URLSearchParams(location.search)

    const token =
      searchParams.get('resetToken')

    if (token) {
      setResetToken(token)
      setResetPasswordMessage('')
    } else {
      setResetPasswordMessage(
        'This password reset link is missing a valid reset token.'
      )
    }
  }, [location.search])

  // 3b - Focus Confirmation Button - 17 Jul 2026
  useEffect(() => {
    if (resetPasswordSuccess) {
      confirmButtonRef.current?.focus()
    }
  }, [resetPasswordSuccess])

  // 3c - Prevent Background Scrolling While Confirmation is Open - 17 Jul 2026
  useEffect(() => {
    if (!resetPasswordSuccess) {
      return undefined
    }

    const originalOverflow =
      document.body.style.overflow

    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow =
        originalOverflow
    }
  }, [resetPasswordSuccess])

  // 4a - Handle Password Input
  const handlePasswordInput = (e) => {
    setNewPassword(e.target.value)
    setResetPasswordMessage('')
  }

  // 4b - Handle Confirm Password Input
  const handleConfirmPasswordInput = (e) => {
    setConfirmPassword(e.target.value)
    setResetPasswordMessage('')
  }

  // 5 - Handle Password Reset Submit
  const handleSubmit = async (e) => {
    e.preventDefault()
    setResetPasswordMessage('')

    // Basic data validation
    if (!resetToken) {
      setResetPasswordMessage(
        'This password reset link is invalid or incomplete.'
      )

      return
    }

    if (!newPassword) {
      setResetPasswordMessage(
        'Please enter a new password.'
      )

      return
    }

    if (!isPasswordValid) {
      setResetPasswordMessage(
        'Your password does not meet all required security rules.'
      )

      return
    }

    if (!confirmPassword) {
      setResetPasswordMessage(
        'Please confirm your new password.'
      )

      return
    }

    if (!passwordsMatch) {
      setResetPasswordMessage(
        'New password and confirmation password must match.'
      )

      return
    }

    // Send the data to server to complete the reset
    try {
      setIsSubmitting(true)

      const response = await fetch(
        `${serverUrl}/users/reset`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            newPassword,
            resetToken,
          }),
        }
      )

      const data = await response.json()

      // Show success only when the request completed successfully
      if (response.ok) {
        setResetPasswordMessage(
          data.message ||
          'Your password has been reset successfully.'
        )

        setResetPasswordSuccess(true)
      } else {
        setResetPasswordMessage(
          data.message ||
          'Unable to reset your password. The link may be invalid or expired.'
        )
      }
    } catch (error) {
      // Error handling
      console.error('Error:', error)

      setResetPasswordMessage(
        'Unable to reset your password. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // 6 - Main Page Content
  const content = (
    <main
      className={`relative flex min-h-screen flex-col overflow-hidden font-poppins transition-colors duration-500 ${pageClass}`}
    >
      {/* 6a - Minimal Background Illumination */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div
          className={`absolute -left-52 -top-52 h-[500px] w-[500px] rounded-full blur-[150px] ${
            isDark
              ? 'bg-orange-600/10'
              : 'bg-orange-200/35'
          }`}
        />

        <div
          className={`absolute -bottom-64 right-[-170px] h-[580px] w-[580px] rounded-full blur-[165px] ${
            isDark
              ? 'bg-orange-900/10'
              : 'bg-orange-100/50'
          }`}
        />
      </div>

      {/* 6b - Theme Toggle */}
      <div
        className={`absolute right-4 top-4 z-30 sm:right-7 sm:top-6 ${
          resetPasswordSuccess
            ? 'pointer-events-none'
            : ''
        }`}
        aria-hidden={resetPasswordSuccess}
      >
        <button
          type="button"
          onClick={handleThemeToggle}
          tabIndex={
            resetPasswordSuccess ? -1 : 0
          }
          aria-label={
            isDark
              ? 'Switch to light theme'
              : 'Switch to dark theme'
          }
          title={
            isDark
              ? 'Switch to light theme'
              : 'Switch to dark theme'
          }
          className={`flex h-9 items-center gap-1 rounded-full border p-1 shadow-sm backdrop-blur-md transition-colors duration-300 ${themeToggleContainerClass}`}
        >
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-full transition-all duration-300 ${
              !isDark
                ? 'bg-orange-500 text-white shadow-sm'
                : 'text-gray-500'
            }`}
          >
            <SunIcon />
          </span>

          <span
            className={`flex h-7 w-7 items-center justify-center rounded-full transition-all duration-300 ${
              isDark
                ? 'bg-orange-500 text-white shadow-sm'
                : 'text-gray-400'
            }`}
          >
            <MoonIcon />
          </span>
        </button>
      </div>

      {/* 7 - Centered Page Layout */}
      <div
        className={`relative z-10 flex flex-1 items-center justify-center px-4 py-6 sm:px-6 sm:py-8 ${
          resetPasswordSuccess
            ? 'pointer-events-none select-none'
            : ''
        }`}
        aria-hidden={resetPasswordSuccess}
      >
        {/* 8 - Reset Password Dialog */}
        <section
          className={`w-full max-w-[560px] rounded-[28px] border px-7 py-7 shadow-xl backdrop-blur-xl transition-colors duration-500 sm:px-10 sm:py-8 ${dialogClass}`}
        >
          {/* 8a - Trinity Logo and Reset Message */}
          <div className="flex items-center gap-5 sm:gap-8">
            {/* 8a1 - Trinity Logo */}
            <Link
              to="/login"
              className="shrink-0"
              aria-label="Return to Trinity sign in"
              tabIndex={
                resetPasswordSuccess ? -1 : 0
              }
            >
              <div
                className={`flex h-14 w-14 items-center justify-center rounded-full bg-black shadow-lg sm:h-16 sm:w-16 ${
                  isDark
                    ? 'shadow-black/50 ring-1 ring-orange-500/20'
                    : 'shadow-gray-400/50'
                }`}
              >
                <EtEtTrinityIcon6SVGComp className="h-10 w-10 text-orange-500 sm:h-12 sm:w-12" />
              </div>
            </Link>

            {/* 8a2 - Reset Message */}
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-semibold uppercase tracking-[0.28em] text-orange-600">
                Secure access
              </p>

              <h1
                className={`mt-1.5 text-lg font-semibold leading-6 tracking-[-0.025em] sm:text-xl sm:leading-7 ${mainTextClass}`}
              >
                Create a fresh password.
              </h1>

              <p
                className={`mt-2 text-xs leading-5 sm:text-sm ${mutedTextClass}`}
              >
                Restore access to your Trinity account.
              </p>
            </div>
          </div>

          {/* 8b - Header Divider */}
          <div
            className={`my-6 border-t ${dividerClass}`}
          />

          {/* 8c - Password Reset Message */}
          {!resetPasswordSuccess &&
            resetPasswordMessage && (
              <div
                aria-live="assertive"
                className={`mb-5 border-l-2 border-red-500 px-4 py-3 text-sm leading-6 ${passwordInputMessageClass}`}
              >
                {resetPasswordMessage}
              </div>
            )}

          {/* 9 - Reset Password Form */}
          {!resetPasswordSuccess && (
            <form
              className="space-y-5"
              onSubmit={handleSubmit}
            >
              {/* 9a - New Password */}
              <PasswordField
                id="password"
                label="New password"
                value={newPassword}
                onChange={handlePasswordInput}
                visible={showNewPassword}
                onToggle={() =>
                  setShowNewPassword(
                    (prev) => !prev
                  )
                }
                autoComplete="new-password"
                describedBy="password-guidance"
                isDark={isDark}
              />

              {/* 9b - Confirm New Password */}
              <PasswordField
                id="confirmPassword"
                label="Confirm new password"
                value={confirmPassword}
                onChange={
                  handleConfirmPasswordInput
                }
                visible={showConfirmPassword}
                onToggle={() =>
                  setShowConfirmPassword(
                    (prev) => !prev
                  )
                }
                autoComplete="new-password"
                describedBy="password-match-status password-guidance"
                isDark={isDark}
              />

              {/* 9c - Password Guidance and Strength */}
              <div
                id="password-guidance"
                className={`rounded-2xl border px-4 py-4 ${guidanceContainerClass}`}
              >
                {/* 9c1 - Strength Header */}
                <div className="flex items-center justify-between gap-4">
                  <p
                    className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${mutedTextClass}`}
                  >
                    Password strength
                  </p>

                  {passwordStrength.label && (
                    <span
                      aria-live="polite"
                      className={`text-[11px] font-semibold ${
                        passwordStrength.label ===
                        'Strong'
                          ? isDark
                            ? 'text-emerald-400'
                            : 'text-emerald-700'
                          : passwordStrength.label ===
                              'Good'
                            ? 'text-orange-500'
                            : isDark
                              ? 'text-red-400'
                              : 'text-red-600'
                      }`}
                    >
                      {passwordStrength.label}
                    </span>
                  )}
                </div>

                {/* 9c2 - Strength Indicator */}
                <div
                  className="mt-2.5 grid grid-cols-3 gap-2"
                  aria-hidden="true"
                >
                  {[1, 2, 3].map((level) => (
                    <span
                      key={level}
                      className={`h-1.5 rounded-full transition-colors duration-300 ${
                        passwordStrength.level >=
                        level
                          ? passwordStrength.label ===
                            'Strong'
                            ? 'bg-emerald-500'
                            : passwordStrength.label ===
                                'Good'
                              ? 'bg-orange-500'
                              : 'bg-red-500'
                          : isDark
                            ? 'bg-gray-800'
                            : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>

                {/* 9c3 - Required Password Rules */}
                <ul className="mt-4 grid grid-cols-1 gap-x-5 gap-y-1.5 sm:grid-cols-2">
                  <PasswordRuleItem
                    met={passwordRules.minLength}
                    isDark={isDark}
                  >
                    Minimum 8 characters
                  </PasswordRuleItem>

                  <PasswordRuleItem
                    met={passwordRules.uppercase}
                    isDark={isDark}
                  >
                    One uppercase letter
                  </PasswordRuleItem>

                  <PasswordRuleItem
                    met={passwordRules.number}
                    isDark={isDark}
                  >
                    One number
                  </PasswordRuleItem>

                  <PasswordRuleItem
                    met={
                      passwordRules.specialCharacter
                    }
                    isDark={isDark}
                  >
                    One special character
                  </PasswordRuleItem>
                </ul>

                {/* 9c4 - Password Match Status */}
                {confirmPassword && (
                  <div
                    id="password-match-status"
                    aria-live="polite"
                    className={`mt-3 flex items-center gap-2 border-t pt-3 text-[11px] ${
                      isDark
                        ? 'border-gray-800'
                        : 'border-gray-200'
                    } ${
                      passwordsMatch
                        ? isDark
                          ? 'text-emerald-400'
                          : 'text-emerald-700'
                        : isDark
                          ? 'text-red-400'
                          : 'text-red-600'
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                        passwordsMatch
                          ? isDark
                            ? 'border-emerald-500/50 bg-emerald-500/15'
                            : 'border-emerald-500/40 bg-emerald-50'
                          : isDark
                            ? 'border-red-500/40 bg-red-500/10'
                            : 'border-red-300 bg-red-50'
                      }`}
                      aria-hidden="true"
                    >
                      {passwordsMatch ? (
                        <CheckIcon />
                      ) : (
                        <span className="text-[10px] font-semibold">
                          ×
                        </span>
                      )}
                    </span>

                    {passwordsMatch
                      ? 'Passwords match'
                      : 'Passwords do not match'}
                  </div>
                )}
              </div>

              {/* 9d - Set New Password Button */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-700 via-orange-600 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-orange-500/25 focus:outline-none focus:ring-4 focus:ring-orange-300/40 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-lg"
              >
                {isSubmitting
                  ? 'Setting new password...'
                  : 'Set new password'}

                {!isSubmitting && <ArrowIcon />}
              </button>
            </form>
          )}

          {/* 10 - Sign In Navigation */}
          {!resetPasswordSuccess && (
            <div
              className={`mt-6 border-t pt-5 ${dividerClass}`}
            >
              <div className="flex items-center justify-center gap-2 text-center">
                <p
                  className={`text-xs ${mutedTextClass}`}
                >
                  Already reset your password?
                </p>

                <Link
                  to="/login"
                  className="text-xs font-medium text-orange-600 transition hover:text-orange-700"
                >
                  Back to sign in
                </Link>
              </div>
            </div>
          )}

          {/* 11 - Copyright */}
          <div
            className={`mt-5 border-t pt-4 text-center ${dividerClass}`}
          >
            <p
              className={`text-[10px] leading-5 ${secondaryTextClass}`}
            >
              © {new Date().getFullYear()} Eternity Solutions Corp.
              All rights reserved.
            </p>
          </div>
        </section>
      </div>

      {/* 12 - Password Reset Success Confirmation */}
      {resetPasswordSuccess && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
          role="presentation"
        >
          {/* 12a - Confirmation Backdrop */}
          <div
            className={`absolute inset-0 ${
              isDark
                ? 'bg-black/80'
                : 'bg-gray-950/60'
            } backdrop-blur-sm`}
            aria-hidden="true"
          />

          {/* 12b - Confirmation Dialog */}
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="reset-success-title"
            aria-describedby="reset-success-message"
            className={`relative z-[101] w-full max-w-[440px] rounded-[28px] border px-7 py-8 text-center shadow-2xl sm:px-10 ${confirmationDialogClass}`}
          >
            {/* 12b1 - Success Icon */}
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg shadow-orange-500/25">
              <SuccessIcon />
            </div>

            {/* 12b2 - Success Message */}
            <p className="mt-5 text-[9px] font-semibold uppercase tracking-[0.28em] text-orange-600">
              Password reset complete
            </p>

            <h2
              id="reset-success-title"
              className={`mt-2 text-xl font-semibold tracking-[-0.025em] sm:text-2xl ${mainTextClass}`}
            >
              {successHeader}
            </h2>

            <p
              id="reset-success-message"
              className={`mt-3 text-sm leading-6 ${mutedTextClass}`}
            >
              {successMsg}
            </p>

            {/* 12b3 - Proceed Button */}
            <button
              ref={confirmButtonRef}
              type="button"
              onClick={handleConfirm}
              className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-700 via-orange-600 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-orange-500/25 focus:outline-none focus:ring-4 focus:ring-orange-300/40"
            >
              Proceed to sign in
              <ArrowIcon />
            </button>
          </section>
        </div>
      )}
    </main>
  )

  return content
}

export default ResetPassword